'use client';

import { projectTopicOptions } from '@/utils/topicOptions';
import { promptTopicFilterNeedsInventory } from '@/utils/promptTopicFilter';

import { Box, Button, Flex, SimpleGrid, Text } from '@chakra-ui/react';
import { IconBuildingStore, IconSearch } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { LoadingIndicator as Spinner } from '@/components/shared/LoadingIndicator';
import { useProject } from '@/contexts/ProjectContext';
import { useBrandRouteScope } from '@/hooks/useBrandRouteScope';
import { useBrandsPageDiscovery } from '@/hooks/useBrandsPageDiscovery';
import { usePipelineJobs } from '@/hooks/usePipelineJobs';
import { usePipelineFilterOptions } from '@/hooks/usePipelineFilterOptions';
import {
  buildDataExplorerBrands,
  searchDataExplorerBrands,
  type DataExplorerBrandFavorite,
} from '@/lib/attune/data-explorer';
import {
  buildLeaderboardBrandHref,
  buildLeaderboardDiscoveryParams,
  LEADERBOARD_DISCOVERY_LIMIT,
} from '@/utils/brandDiscovery';
import { DataExplorerCard } from './DataExplorerCard';

/** Mounted only on the Brands tab, so opening the module gallery does not query brand data. */
export function DataExplorerBrands({
  query,
  onNavigate,
  favorites,
  favoritesReady = true,
  onToggleFavorite,
}: {
  query: string;
  onNavigate: (href: string) => void;
  favorites: readonly DataExplorerBrandFavorite[];
  favoritesReady?: boolean;
  onToggleFavorite: (brand: DataExplorerBrandFavorite) => void;
}) {
  const { currentProject } = useProject();
  const settings = currentProject?.brand_settings;
  const promptGroups = useMemo(
    () => projectTopicOptions(currentProject),
    [currentProject?.prompt_groups, currentProject?.topic_options]
  );
  const {
    prompts,
    promptsReady,
    error: promptsError,
    refresh: refreshPrompts,
  } = usePipelineFilterOptions({ sections: ['prompts'] });
  const {
    jobs,
    loading: jobsLoading,
    jobsTrustworthy,
    error: jobsError,
    refresh: refreshJobs,
  } = usePipelineJobs();
  const {
    weekSelection,
    periodGranularity,
    scopedEngine,
    scopedPromptIds,
    selectedPromptId,
    resolvedScope,
    sqlPromptGroupId,
    promptGroupOrigin,
    promptSubtopicId,
    topicScopeReady,
  } = useBrandRouteScope({
    promptGroups,
    prompts,
    promptsReady,
    mode: 'with-week',
    jobs,
    jobsTrustworthy,
    defaultToLast7Days: true,
  });
  const brandGroups = useMemo(
    () => [
      ...(settings?.brand_name?.trim()
        ? [
            {
              canonical_name: settings.brand_name.trim(),
              variations: (settings.brand_variations || [])
                .map(value => value.trim())
                .filter(Boolean),
            },
          ]
        : []),
      ...(currentProject?.brand_merge_groups || [])
        .filter(group => group.canonical_name?.trim())
        .map(group => ({
          canonical_name: group.canonical_name.trim(),
          variations: (group.variations || []).map(value => value.trim()).filter(Boolean),
        })),
    ],
    [settings, currentProject?.brand_merge_groups]
  );
  const params = useMemo(
    () =>
      buildLeaderboardDiscoveryParams({
        weekSelection,
        jobsTrustworthy: jobsTrustworthy && topicScopeReady,
        engine: scopedEngine,
        promptGroupId: sqlPromptGroupId,
        promptGroupOrigin,
        promptSubtopicId,
        promptIds: scopedPromptIds,
        explicitPromptIds: selectedPromptId ? [selectedPromptId] : scopedPromptIds,
        brandGroups,
      }),
    [
      weekSelection,
      jobsTrustworthy,
      topicScopeReady,
      scopedEngine,
      scopedPromptIds,
      selectedPromptId,
      brandGroups,
      sqlPromptGroupId,
      promptGroupOrigin,
      promptSubtopicId,
    ]
  );
  const {
    brands,
    loading: brandsLoading,
    error: brandsError,
    refresh,
  } = useBrandsPageDiscovery(params);
  const needsPromptInventory = promptTopicFilterNeedsInventory(resolvedScope.topicFilter);
  const promptInventoryError = needsPromptInventory ? promptsError : null;
  const promptInventoryPending = needsPromptInventory && !promptsReady && !promptInventoryError;
  const loading =
    jobsLoading || (!jobsTrustworthy && !jobsError) || brandsLoading || promptInventoryPending;
  const error = jobsError || promptInventoryError || brandsError;
  const directory = useMemo(
    () =>
      topicScopeReady
        ? buildDataExplorerBrands(
            brands,
            settings
              ? {
                  name: settings.brand_name,
                  slug: settings.brand_slug,
                  website: settings.website_url,
                  aliases: settings.brand_variations,
                }
              : undefined,
            currentProject?.brand_merge_groups
          )
        : [],
    [brands, settings, currentProject?.brand_merge_groups, topicScopeReady]
  );
  const favoriteSlugs = useMemo(() => new Set(favorites.map(brand => brand.slug)), [favorites]);
  const filtered = useMemo(
    () =>
      searchDataExplorerBrands(directory, query).sort(
        (a, b) => Number(favoriteSlugs.has(b.slug)) - Number(favoriteSlugs.has(a.slug))
      ),
    [directory, query, favoriteSlugs]
  );
  const [page, setPage] = useState({ key: '', count: 40 });
  const pageKey = `${currentProject?.id}:${query}`;
  const visibleCount = page.key === pageKey ? page.count : 40;

  return (
    <Box aria-busy={loading}>
      <Flex align="start" justify="space-between" gap="4" mb="5">
        <Box>
          <Text as="h3" fontWeight="600" fontSize="md">
            Brands in your market
          </Text>
          <Text fontSize="sm" color="ui.text.secondary" mt="1">
            Brands from Market Leaders, using the same date and question filters.
          </Text>
        </Box>
        <Box color="ui.text.tertiary" pt="1" aria-hidden="true">
          <IconBuildingStore size={22} stroke={1.5} />
        </Box>
      </Flex>
      {loading ? (
        <Flex role="status" align="center" gap="2" color="ui.text.secondary" fontSize="sm" mb="4">
          <Spinner size="sm" /> Loading brands…
        </Flex>
      ) : error ? (
        <Flex
          role="alert"
          align="center"
          justify="space-between"
          gap="3"
          p="3"
          mb="4"
          bg="ui.surface.input"
          borderRadius="10px"
        >
          <Text fontSize="sm">Couldn’t load the market’s brands.</Text>
          <Button
            size="xs"
            variant="outline"
            onClick={() =>
              void (jobsError ? refreshJobs() : promptInventoryError ? refreshPrompts() : refresh())
            }
          >
            Try again
          </Button>
        </Flex>
      ) : (
        <Text role="status" fontSize="xs" color="ui.text.tertiary" mb="3">
          {filtered.length} {filtered.length === 1 ? 'brand' : 'brands'}
          {query.trim() ? ` matching “${query.trim()}”` : ''}
        </Text>
      )}
      {!filtered.length && !loading && !error ? (
        <Flex direction="column" align="center" textAlign="center" gap="3" py="12">
          <IconSearch size={28} stroke={1.4} />
          <Text fontWeight="600">
            {query.trim() ? 'No matching brands' : 'Your market starts here'}
          </Text>
          <Text color="ui.text.secondary" fontSize="sm">
            {query.trim()
              ? 'Try a brand name or website.'
              : 'Brands appear here as your project collects AI answers.'}
          </Text>
        </Flex>
      ) : null}
      <SimpleGrid minChildWidth={{ base: '240px', md: '280px' }} gap="2.5">
        {filtered.slice(0, visibleCount).map(brand => {
          const href = buildLeaderboardBrandHref(brand.slug, {
            weekSelection,
            periodGranularity,
            engine: scopedEngine,
            promptGroupId: resolvedScope.promptGroupId,
            promptGroupOrigin,
            promptSubtopicId,
            promptIds: selectedPromptId ? [selectedPromptId] : scopedPromptIds,
            topicFilter: resolvedScope.topicFilter,
            promptId: selectedPromptId,
          });
          const isFavorite = favoriteSlugs.has(brand.slug);
          return (
            <DataExplorerCard
              key={brand.slug}
              href={href}
              title={brand.name}
              subtitle={[brand.isOwn ? 'Your brand' : '', brand.domain].filter(Boolean).join(' · ')}
              description={
                brand.description || 'Explore how this brand appears in your market’s AI answers.'
              }
              icon={brand.name
                .split(/\s+/)
                .slice(0, 2)
                .map(word => word[0])
                .join('')
                .toUpperCase()}
              details={brand.metrics || []}
              favorite={isFavorite}
              favoritesReady={favoritesReady}
              onNavigate={event => {
                if (
                  event.metaKey ||
                  event.ctrlKey ||
                  event.shiftKey ||
                  event.altKey ||
                  event.button !== 0
                )
                  return;
                event.preventDefault();
                onNavigate(href);
              }}
              onToggleFavorite={() => {
                const savedBrand = { ...brand };
                delete savedBrand.metrics;
                onToggleFavorite({ ...savedBrand, href });
              }}
            />
          );
        })}
      </SimpleGrid>
      {filtered.length > visibleCount ? (
        <Button
          mt="4"
          size="sm"
          variant="outline"
          onClick={() => setPage({ key: pageKey, count: visibleCount + 40 })}
        >
          Show more brands
        </Button>
      ) : null}
      {brands.length >= LEADERBOARD_DISCOVERY_LIMIT ? (
        <Text mt="4" fontSize="xs" color="ui.text.tertiary">
          Searching the same top {LEADERBOARD_DISCOVERY_LIMIT} brands as Market Leaders.
        </Text>
      ) : null}
    </Box>
  );
}
